import styled from '@emotion/styled';
import type { LabelModel } from '@hotjar/labels';
import { GenericShortListFilter } from '@hotjar/global-filters';
import { useAvailableLabels, DEFAULT_LABEL_ICONS } from '@hotjar/labels';
import { Icon, Text, tokens } from '@hotjar/hotsauce';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Row = styled.div`
  height: 18px;
  display: flex;
  align-items: center;
  position: relative;
  top: 1px;
`;

const LabelIcon = styled(Icon)`
  margin: 0px;
  margin-right: ${tokens.spacer.x1};
`;

const BlankIcon = styled.span`
  margin: 0px;
  margin-right: ${tokens.spacer.x1};
  width: ${tokens.spacer.x2};
`;

const LabelRow = ({ iconName, name, keyName }: Omit<LabelModel, 'id'>): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Row>
      {iconName !== 'unknown' ? (
        <LabelIcon icon={DEFAULT_LABEL_ICONS[iconName] ?? iconName} size="m" />
      ) : (
        <BlankIcon />
      )}
      <Text textType="caption" color="text_default">
        {keyName ? t(keyName) : name}
      </Text>
    </Row>
  );
};

interface LabelFilterProps {
  onApply: (labelIds: Set<string>) => void;
  selectedLabels: Set<string>;
  siteId: Parameters<typeof useAvailableLabels>[0];
}

export const LabelFilter = ({
  onApply,
  selectedLabels = new Set(),
  siteId,
}: LabelFilterProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { data: labels } = useAvailableLabels(siteId, 'all');
  const [currentValue, setCurrentValue] = useState([...selectedLabels]);

  // reset the current values after route change
  useEffect(() => {
    setCurrentValue([...selectedLabels]);
  }, [setCurrentValue, selectedLabels]);

  if (!labels) {
    return null;
  }

  const options = labels.map((x) => ({
    value: x.id,
    label: x.keyName ? t(x.keyName) : x.name,
    content: <LabelRow name={x.name} iconName={x.iconName} keyName={x.keyName} />,
  }));

  const onChange = (value?: string[]) => {
    const newVal = value ?? [];
    setCurrentValue(newVal);
    onApply(new Set(newVal));
  };

  const onRemove = () => onChange([]);

  return (
    <GenericShortListFilter
      renderLabel={false}
      filterName={t('highlights:filters.labels.filter-name')}
      label2={currentValue.length ? undefined : 'Any'}
      iconName="tag"
      currentValue={currentValue}
      onRemove={currentValue.length ? onRemove : undefined}
      isInactive={currentValue.length === 0}
      optionList={options}
      showDropdownCaret
      onApply={onChange}
    />
  );
};
